import { useState } from 'react';
import {
  Badge,
  Card,
  CardDescription,
  CardTitle,
  EmptyState,
  ErrorState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  Tooltip,
  formatNumber,
  formatPercent,
} from '@mgdlead/ui';
import { Megaphone, Users } from 'lucide-react';

import { PageHeader } from '@mgdlead/ui';
import { FormAlert } from '@mgdlead/ui';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { workspacePath } from '../../../features/workspace/navigation';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';
import {
  useAnalyticsBreakdown,
  type AnalyticsCampaignRow,
  type AnalyticsGroupRow,
} from '../../../lib/api';
import { CampaignPerformanceCard } from '../../../features/campaigns/CampaignPerformance';
import { PeriodFilter } from './AnalyticsShell';
import { METRICA, type PeriodoId } from './analytics-ui';

const TRACO = '—';

/**
 * Gestão — a comparação: por grupo e por campanha, no mesmo período.
 *
 * ### O caso real que esta tela precisa mostrar sem mentir
 *
 * Em produção há dois grupos ao mesmo tempo: um **varrido** (sabemos quem são as 550 pessoas)
 * e um **só importado** (sabemos que são 980, não sabemos quem). São dois estados de
 * conhecimento diferentes sobre o mesmo tipo de linha, e uma tabela que mostrasse só "550" e
 * "980" daria a entender que sabe o mesmo dos dois.
 *
 * Por isso a coluna "Pessoas" mostra **conhecidas de total** e o grupo nunca varrido leva o
 * selo "só a contagem". Evasão, permanência e retenção só existem para o primeiro.
 */
export function GestaoPage() {
  const { workspace } = useWorkspace();
  const [periodo, setPeriodo] = useState<PeriodoId>('D30');
  useDocumentTitle(`Gestão · ${workspace.name}`);

  const breakdown = useAnalyticsBreakdown(workspace.id, periodo);

  return (
    <>
      <PageHeader
        title="Gestão"
        description="Compare grupos e campanhas no mesmo recorte de tempo."
      />

      <PeriodFilter value={periodo} onChange={setPeriodo} timezone={workspace.timezone} />

      {breakdown.isError ? (
        <ErrorState
          title="Não foi possível carregar a comparação"
          description={breakdown.error.message}
          onRetry={() => void breakdown.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {/* O dinheiro primeiro: era o rodapé, debaixo de 23 grupos (dono, 06/10). */}
          <CampaignPerformanceCard
            rows={breakdown.data?.funil}
            carregando={breakdown.isPending}
            titulo="Do anúncio ao grupo"
            campaignHref={(id) => workspacePath(workspace.slug, `campanhas/${id}`)}
          />
          <GruposCard
            rows={breakdown.data?.groups}
            carregando={breakdown.isPending}
            semMedicao={breakdown.data?.observed.firstDay === null}
          />
          <CampanhasCard rows={breakdown.data?.campaigns} carregando={breakdown.isPending} />
        </div>
      )}
    </>
  );
}

/* ========================================================================== */
/*  Grupos                                                                     */
/* ========================================================================== */

function GruposCard({
  rows,
  carregando,
  semMedicao,
}: {
  rows: AnalyticsGroupRow[] | undefined;
  carregando: boolean;
  semMedicao: boolean | undefined;
}) {
  if (carregando || !rows) {
    return (
      <Card padded={false} className="p-3 md:p-4">
        <CardTitle>Grupos no período</CardTitle>
        <div className="mt-3 h-40 animate-pulse rounded-md bg-muted" />
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card padded={false} className="p-3 md:p-4">
        <CardTitle>Grupos no período</CardTitle>
        <EmptyState
          kind="no-data"
          icon={Users}
          title="Nenhum grupo monitorado"
          description="Importe um grupo em Grupos para ele aparecer aqui."
          compact
        />
      </Card>
    );
  }

  const semVarredura = rows.filter((g) => g.syncedAt === null).length;

  return (
    <Card padded={false} className="p-3 md:p-4">
      <CardTitle>Grupos no período</CardTitle>
      <CardDescription>
        Entradas, saídas e evasão de cada grupo no recorte escolhido.
      </CardDescription>

      {semVarredura > 0 ? (
        <FormAlert tone="info" className="mt-3">
          {semVarredura === 1
            ? 'Um grupo ainda não foi varrido'
            : `${semVarredura} grupos ainda não foram varridos`}
          : sabemos <strong>quantas</strong> pessoas estão lá, não <strong>quem</strong> são.
          Enquanto isso, permanência e retenção não existem para{' '}
          {semVarredura === 1 ? 'ele' : 'eles'} — e a evasão é medida contra a contagem, não contra
          identidades. Use “Sincronizar pessoas” na tela de Grupos.
        </FormAlert>
      ) : null}

      <Table caption="Grupos no período" density="dense" className="mt-3" zebra>
        <TableHeader>
          <TableRow>
            <TableHead>Grupo</TableHead>
            <TableHead>Pessoas</TableHead>
            <TableHead>Entradas</TableHead>
            <TableHead>Saídas</TableHead>
            <TableHead>Remoções</TableHead>
            <TableHead>Saldo</TableHead>
            <TableHead>Evasão</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((g) => (
            <TableRow key={g.groupId}>
              <TableRowHeader>
                <span className="block truncate">{g.name ?? 'Sem nome no WhatsApp'}</span>
                {g.syncedAt === null ? (
                  <Badge tone="neutral" className="mt-1">
                    só a contagem
                  </Badge>
                ) : null}
              </TableRowHeader>

              <TableCell nowrap>
                {g.participantsNow === null ? (
                  /*
                   * Nunca contado. `null` não é `0` (invariante 8): "não sabemos quantos" é
                   * diferente de "o grupo está vazio", e a segunda leitura faria alguém achar
                   * que o grupo pode receber 980 pessoas.
                   */
                  <Tooltip content="Este grupo nunca foi contado. Não é o mesmo que estar vazio.">
                    <span tabIndex={0} className="cursor-help text-muted-foreground">
                      {TRACO}
                    </span>
                  </Tooltip>
                ) : (
                  <span className="nums">
                    {formatNumber(g.participantsKnown)}
                    <span className="text-muted-foreground">
                      {' '}
                      de {formatNumber(g.participantsNow)}
                    </span>
                  </span>
                )}
              </TableCell>

              <TableCell numeric>{semMedicao ? TRACO : formatNumber(g.joins)}</TableCell>
              <TableCell numeric>{semMedicao ? TRACO : formatNumber(g.leaves)}</TableCell>
              <TableCell numeric>{semMedicao ? TRACO : formatNumber(g.adminRemovals)}</TableCell>
              <TableCell numeric>{semMedicao ? TRACO : formatNumber(g.balance)}</TableCell>
              <TableCell nowrap>
                {g.churn === null ? (
                  <Tooltip content={METRICA.evasao.semDados}>
                    <span tabIndex={0} className="cursor-help text-muted-foreground">
                      {TRACO}
                    </span>
                  </Tooltip>
                ) : (
                  <span className="nums">{formatPercent(g.churn, 1)}</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/* ========================================================================== */
/*  Campanhas                                                                  */
/* ========================================================================== */

function CampanhasCard({
  rows,
  carregando,
}: {
  rows: AnalyticsCampaignRow[] | undefined;
  carregando: boolean;
}) {
  if (carregando || !rows) {
    return (
      <Card padded={false} className="p-3 md:p-4">
        <CardTitle>Campanhas no período</CardTitle>
        <div className="mt-3 h-32 animate-pulse rounded-md bg-muted" />
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card padded={false} className="p-3 md:p-4">
        <CardTitle>Campanhas no período</CardTitle>
        <EmptyState
          kind="no-data"
          icon={Megaphone}
          title="Nenhuma campanha cadastrada"
          description="Campanhas agrupam grupos e links sob um mesmo código. Crie uma em Campanhas."
          compact
        />
      </Card>
    );
  }

  const totalCliques = rows.reduce((acc, r) => acc + r.clicks, 0);

  return (
    <Card padded={false} className="p-3 md:p-4">
      <CardTitle>Campanhas no período</CardTitle>
      <CardDescription>Cliques, origem e casamento com entradas, por campanha.</CardDescription>

      {totalCliques === 0 ? (
        <FormAlert tone="info" className="mt-3">
          Nenhuma campanha recebeu clique no período. Os zeros abaixo são{' '}
          <strong>zeros medidos</strong>: as campanhas existem, o período foi medido e não houve
          clique. Isso é diferente de “sem dados” — e é o que se espera antes de o primeiro anúncio
          entrar no ar.
        </FormAlert>
      ) : null}

      <Table caption="Campanhas no período" density="dense" className="mt-3" zebra>
        <TableHeader>
          <TableRow>
            <TableHead>Campanha</TableHead>
            <TableHead>Cliques</TableHead>
            <TableHead>Visitantes</TableHead>
            <TableHead>De anúncio</TableHead>
            <TableHead>Sem origem</TableHead>
            <TableHead>Entradas casadas</TableHead>
            <TableHead>Taxa de entrada</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((c) => (
            <TableRow key={c.campaignId}>
              <TableRowHeader>
                <span className="block truncate">{c.name}</span>
                <span className="nums block text-2xs text-muted-foreground">{c.code}</span>
              </TableRowHeader>
              <TableCell numeric>{formatNumber(c.clicks)}</TableCell>
              <TableCell numeric>{formatNumber(c.uniqueVisitors)}</TableCell>
              <TableCell numeric>{formatNumber(c.metaAds)}</TableCell>
              <TableCell numeric>{formatNumber(c.noOrigin)}</TableCell>
              <TableCell numeric>{formatNumber(c.sessionsMatched)}</TableCell>
              <TableCell nowrap>
                {c.entryRate === null ? (
                  <Tooltip content={METRICA.taxaEntrada.semDados}>
                    <span tabIndex={0} className="cursor-help text-muted-foreground">
                      {TRACO}
                    </span>
                  </Tooltip>
                ) : (
                  /*
                   * Invariante 9: número com atribuição carrega o grau de confiança junto.
                   * O casamento clique-entrada é PROBABILÍSTICO (ADR-0002), então a taxa é
                   * sempre uma estimativa por baixo — e isso vai colado no número, não numa
                   * nota de rodapé que ninguém lê.
                   */
                  <Tooltip content="Estimativa por baixo: o salto para o WhatsApp não devolve identificador, então o casamento é probabilístico.">
                    <span tabIndex={0} className="nums cursor-help">
                      ≥ {formatPercent(c.entryRate, 1)}
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
